// ===============================================
// 🛠️ SUPPORT - COMPONENTE PRINCIPAL CON SUB-TABS
// ===============================================

import React, { useState, useEffect } from 'react';
import { 
  Shield,
  BarChart3,
  Ticket,
  MessageCircle,
  HelpCircle,
  FileText,
  Settings,
  Users,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Star,
  Phone,
  Mail,
  Bot,
  Headphones,
  Zap
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ticketService } from '../services/ticketService';
// Importar sub-componentes (que crearemos)
import SupportDashboard from './Support/SupportDashboard';
import TicketSystem from './Support/TicketSystem';
import LiveChat from './Support/LiveChat';
import FAQSection from './Support/FAQSection';
import DocumentationViewer from './Support/DocumentationViewer';


// ===============================================
// 🔧 INTERFACES
// ===============================================

interface SupportStats {
  total_tickets: number;
  open_tickets: number;
  resolved_today: number;
  avg_response_time: string;
  satisfaction_rating: number;
  active_chats: number;
  total_users_helped: number;
  knowledge_base_articles: number;
}

interface SupportSubTab {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<any>;
  description: string;
  badge?: number;
  isEnabled: boolean;
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const Support: React.FC = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState<SupportStats | null>(null);
  const [loading, setLoading] = useState(true);

  // ===============================================
  // 🎨 CLASES DE ESTILO
  // ===============================================

  const cardClasses = `rounded-lg shadow-md transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClasses = isDark ? 'border-gray-600' : 'border-gray-300';

  // ===============================================
  // 🔧 CONFIGURACIÓN DE SUB-TABS
  // ===============================================

  const subTabs: SupportSubTab[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      component: SupportDashboard,
      description: 'Vista general del sistema de soporte',
      isEnabled: true
    },
    {
      id: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      component: TicketSystem,
      description: 'Gestión integral de tickets de soporte',
      badge: stats?.open_tickets,
      isEnabled: true
    },
    {
      id: 'chat',
      label: 'Chat en Vivo',
      icon: MessageCircle,
      component: LiveChat,
      description: 'Sistema de chat en tiempo real',
      badge: stats?.active_chats,
      isEnabled: true
    },
    {
      id: 'faq',
      label: 'FAQ',
      icon: HelpCircle,
      component: FAQSection,
      description: 'Preguntas frecuentes y base de conocimientos',
      isEnabled: true
    },
    {
      id: 'docs',
      label: 'Documentación',
      icon: FileText,
      component: DocumentationViewer,
      description: 'Manuales técnicos y documentos',
      isEnabled: true
    }
  ];

  // ===============================================
  // 🔄 EFECTOS Y CARGA DE DATOS
  // ===============================================

  useEffect(() => {
    loadSupportStats();
  }, []);

  const loadSupportStats = async () => {
  setLoading(true);
  try {
    console.log('📊 Cargando estadísticas reales de tickets...');
    
    // ✅ OBTENER DATOS REALES DE TICKETS
    const ticketsResponse = await ticketService.getTickets({ limit: 1000 });
    const allTickets = ticketsResponse.tickets || [];
    
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Calcular estadísticas reales
    const realStats: SupportStats = {
      total_tickets: allTickets.length,
      open_tickets: allTickets.filter(t => t.status === 'open').length,
      resolved_today: allTickets.filter(t => {
        const resolvedDate = new Date(t.updated_at);
        return t.status === 'resolved' && resolvedDate >= startOfDay;
      }).length,
      avg_response_time: '2.3 horas', // Placeholder - calcular si tienes timestamps
      satisfaction_rating: (() => {
        const ratedTickets = allTickets.filter(t => t.rating && t.rating > 0);
        if (ratedTickets.length === 0) return 4.7;
        const avgRating = ratedTickets.reduce((sum, t) => sum + (t.rating || 0), 0) / ratedTickets.length;
        return Math.round(avgRating * 10) / 10;
      })(),
      active_chats: allTickets.filter(t => t.status === 'in_progress').length,
      total_users_helped: [...new Set(allTickets.map(t => t.user_email))].length,
      knowledge_base_articles: 156 // Placeholder estático
    };
    
    console.log('✅ Estadísticas reales calculadas:', realStats);
    setStats(realStats);
    updateHeaderNumbers(realStats); // ← AGREGAR ESTA LÍNEA
  } catch (error) {
    console.error('❌ Error cargando estadísticas reales:', error);
    
    // Fallback a datos mock si falla la API
    const mockStats: SupportStats = {
      total_tickets: 0,
      open_tickets: 0,
      resolved_today: 0,
      avg_response_time: '0 horas',
      satisfaction_rating: 0,
      active_chats: 0,
      total_users_helped: 0,
      knowledge_base_articles: 0
    };
    
    setStats(mockStats);
  } finally {
    setLoading(false);
  }
};

// ✅ AGREGAR useEffect para actualizar estadísticas automáticamente:
useEffect(() => {
  loadSupportStats();
  
  // Actualizar cada 30 segundos
  const interval = setInterval(loadSupportStats, 30000);
  
  return () => clearInterval(interval);
}, []);

// ✅ Función para actualizar números del header superior
const updateHeaderNumbers = (stats: SupportStats) => {
  try {
    // Buscar elementos del header superior por clase o ID
    const headerElements = {
      ticketsAbiertos: document.querySelector('[data-stat="tickets-abiertos"]'),
      resueltosHoy: document.querySelector('[data-stat="resueltos-hoy"]'), 
      satisfaccion: document.querySelector('[data-stat="satisfaccion"]'),
      chatsActivos: document.querySelector('[data-stat="chats-activos"]')
    };
    
    // Actualizar solo si los elementos existen
    if (headerElements.ticketsAbiertos) {
      headerElements.ticketsAbiertos.textContent = stats.open_tickets.toString();
    }
    if (headerElements.resueltosHoy) {
      headerElements.resueltosHoy.textContent = stats.resolved_today.toString();
    }
    if (headerElements.satisfaccion) {
      headerElements.satisfaccion.textContent = stats.satisfaction_rating.toFixed(1);
    }
    if (headerElements.chatsActivos) {
      headerElements.chatsActivos.textContent = stats.active_chats.toString();
    }
    
    console.log('✅ Header superior actualizado con datos reales');
  } catch (error) {
    console.warn('⚠️ No se pudo actualizar header superior:', error);
  }
};





  // ===============================================
  // 🎯 FUNCIONES AUXILIARES
  // ===============================================

  const getActiveTabComponent = () => {
    const activeTabConfig = subTabs.find(tab => tab.id === activeTab);
    if (!activeTabConfig) return null;

    const Component = activeTabConfig.component;
    return <Component stats={stats} onStatsUpdate={setStats} />;
  };

  const getStatusColor = (value: number, thresholds: { good: number; warning: number }) => {
    if (value >= thresholds.good) return 'text-green-600';
    if (value >= thresholds.warning) return 'text-yellow-600';
    return 'text-red-600';
  };

  // ===============================================
  // 🎨 RENDER
  // ===============================================

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* ✅ HEADER PRINCIPAL */}
      <div className={`${cardClasses} m-6 mb-0`}>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Shield className="w-10 h-10 text-blue-600" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                </div>
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold ${textClasses}`}>
                  Centro de Soporte Técnico
                </h1>
                <p className={`${subTextClasses} mt-1`}>
                  Sistema integral de soporte especializado en motores Caterpillar
                </p>
              </div>
            </div>

            {/* Métricas rápidas */}
            {stats && !loading && (
              <div className="hidden lg:flex items-center space-x-6">
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getStatusColor(stats.open_tickets, { good: 0, warning: 20 })}`}>
                    {stats?.total_tickets || 0}
                  </div>
                  <div className={`text-xs ${subTextClasses}`}>Total Tickets</div>
                </div>
                
                <div className="text-center">
                  <div className={`text-2xl font-bold text-green-600`}>
                    {stats?.open_tickets || 0}
                  </div>
                  <div className={`text-xs ${subTextClasses}`}>Tickets Abiertos</div>
                </div>
                
                <div className="text-center">
                  <div className={`text-2xl font-bold text-blue-600`}>
                     {stats?.resolved_today || 0}
                  </div>
                  <div className={`text-xs ${subTextClasses}`}>Resueltos Hoy</div>
                </div>
                
                <div className="text-center hover:shadow-lg transition-shadow">
                  <div className={`text-2xl font-bold ${getStatusColor(stats.active_chats, { good: 10, warning: 5 })}`}>
                   {stats?.satisfaction_rating?.toFixed(1) || '0.0'}
                  </div>
                  <div className={`text-xs ${subTextClasses}`}>Satisfacción</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ NAVEGACIÓN DE SUB-TABS */}
        <div className={`border-t ${borderClasses} px-6`}>
          <nav className="flex space-x-0">
            {subTabs
              .filter(tab => tab.isEnabled)
              .map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center px-6 py-4 text-sm font-medium transition-all duration-200 border-b-2 ${
                      isActive
                        ? 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : `border-transparent ${subTextClasses} hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700`
                    }`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-blue-600' : ''}`} />
                    {tab.label}
                    
                    {/* Badge de notificación */}
                    {tab.badge && tab.badge > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {tab.badge > 99 ? '99+' : tab.badge}
                      </span>
                    )}
                    
                    {/* Indicador de pestaña activa */}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>
                    )}
                  </button>
                );
              })}
          </nav>
        </div>
      </div>

      {/* ✅ CONTENIDO PRINCIPAL */}
      <div className="p-6 pt-0">
        {loading ? (
          <div className={`${cardClasses} p-12`}>
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`${textClasses} text-lg`}>
                Cargando sistema de soporte...
              </span>
            </div>
          </div>
        ) : (
          <div className={`${cardClasses} min-h-[600px]`}>
            {getActiveTabComponent()}
          </div>
        )}
      </div>

      {/* ✅ SIDEBAR DE ACCESO RÁPIDO (Opcional) */}
      <div className="fixed bottom-6 right-6 z-50 space-y-2">
        
        {/* Chat rápido */}
        <button
          onClick={() => setActiveTab('chat')}
          className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 ${
            activeTab === 'chat'
              ? 'bg-blue-600 text-white scale-110'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:scale-105'
          }`}
          title="Chat de soporte rápido"
        >
          <MessageCircle className="w-5 h-5" />
          {stats && stats.active_chats > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white font-bold">
              {stats.active_chats}
            </div>
          )}
        </button>

        {/* Nuevo ticket rápido */}
        <button
          onClick={() => setActiveTab('tickets')}
          className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 ${
            activeTab === 'tickets'
              ? 'bg-green-600 text-white scale-110'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:scale-105'
          }`}
          title="Crear ticket rápido"
        >
          <Ticket className="w-5 h-5" />
        </button>

        {/* FAQ rápido */}
        <button
          onClick={() => setActiveTab('faq')}
          className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all duration-200 ${
            activeTab === 'faq'
              ? 'bg-yellow-600 text-white scale-110'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:scale-105'
          }`}
          title="Preguntas frecuentes"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      {/* ✅ INDICADOR DE ESTADO DEL SERVICIO */}
      <div className="fixed top-6 right-6 z-40">
        <div className={`flex items-center space-x-2 px-3 py-2 rounded-full shadow-md ${
          isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className={`text-xs font-medium ${textClasses}`}>
            Sistema Operativo
          </span>
        </div>
      </div>
    </div>
  );
};

export default Support;